/**
 * @file useSortState.ts
 * @description Generic hook for managing server-side sort state.
 *
 * Produces: order[0][0]=<column>  &  order[0][1]=ASC|DESC
 *
 * Usage:
 *   const { sortColumn, order, direction, toggleOrder, resetOrder } =
 *     useSortState('createdAt', 'DESC');
 *
 *   // Pass into queryParams:
 *   { sortColumn, order }
 *
 *   // On any column header:
 *   <TableHead onClick={() => toggleOrder('createdAt')}>
 *     Created At <SortIcon dir={direction('createdAt')} />
 *   </TableHead>
 *
 * Click cycle on the SAME column:  DESC -> ASC -> DESC -> ASC ...
 * Clicking a DIFFERENT column resets to DESC on that column.
 */

import { useState, useCallback } from 'react';

export type SortOrder = 'ASC' | 'DESC';

export interface UseSortStateReturn {
  /** Current sort column key  (maps to order[0][0]) */
  sortColumn: string;
  /** Current sort direction   (maps to order[0][1]) */
  order: SortOrder;
  /**
   * Toggle sort for the given column.
   * - New column   ? DESC
   * - Same column  ? flip ASC <-> DESC
   */
  toggleOrder: (column: string) => void;
  /** Returns 'asc' | 'desc' for the given column, or false if not sorted */
  direction: (column: string) => 'asc' | 'desc' | false;
  /** Reset sort to constructor defaults */
  resetOrder: () => void;
}

/**
 * @param defaultColumn - Column sorted by default
 * @param defaultOrder  - Direction for the default column (default: 'DESC')
 */
export function useSortState(
  defaultColumn: string = 'createdAt',
  defaultOrder: SortOrder = 'DESC',
): UseSortStateReturn {
  const [sortColumn, setSortColumn] = useState<string>(defaultColumn);
  const [order, setOrder] = useState<SortOrder>(defaultOrder);

  const toggleOrder = useCallback(
    (column: string) => {
      if (sortColumn !== column) {
        // New column -> start DESC
        setSortColumn(column);
        setOrder('DESC');
      } else {
        // Same column -> flip
        setOrder((prev) => (prev === 'DESC' ? 'ASC' : 'DESC'));
      }
    },
    [sortColumn],
  );

  const direction = useCallback(
    (column: string): 'asc' | 'desc' | false => {
      if (sortColumn !== column) return false;
      return order === 'ASC' ? 'asc' : 'desc';
    },
    [sortColumn, order],
  );

  const resetOrder = useCallback(() => {
    setSortColumn(defaultColumn);
    setOrder(defaultOrder);
  }, [defaultColumn, defaultOrder]);

  return { sortColumn, order, toggleOrder, direction, resetOrder };
}
